import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { observer } from "mobx-react";
import { automationStore, emailStore, userStore, generalSettingStore, atsStore, candidateOwnerSettingStore, featureStore, rappidStore } from "stores";
import stepEmailIcon from "assets/img/automation/step-email.svg";
import SubmitButton from "./SubmitButton";
import Select from 'react-select';
import { RappidNodeType, RappidText } from "helpers/Constants";
import { FormGroup, Input, InputGroupAddon, InputGroup, Label, Row, Col, UncontrolledTooltip, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Collapse } from "reactstrap";
import HeaderForm from "./HeaderForm";
import { toJS } from "mobx";
import { EditRappidNode } from "components/Rappid/RappidCanvasFunctions";
import { Constants, Enum, Validator } from "helpers";
import { SelectAutomation } from "_globalStyle";
import { EmailEditorButton, SidebarFormSelect, SenderSectionContainer, SendFromContainer, SendFromLabel, SendFromButtonGroup, SendFromDropdownContainer, SenderModeButton, SenderModeSplit, SenderModeCaret, SenderFromCard, SenderFromCardLabel, SenderFromName, SenderFromDetail, DeliveryAccordionHeader, DeliveryAccordionOptional, DeliveryAccordionChevron, DeliveryOption, DeliveryOptionCheckbox, DeliveryOptionText, DeliveryOptionTitle, DeliveryOptionDescription, DeliveryOptionSettings, DeliveryOptionDivider, ScheduleDayButtonRow, ScheduleDayButton } from "components/Automation/Styled";
import { AutomationLabelSchedule } from "../Styled";
import Alert from 'react-s-alert';
import DatePicker from 'react-datepicker';
import TimeInput from "./TimeInput";
import moment from "moment";
import CompactMergeTagsDropdown from "components/Automation/components/CompactMergeTagsDropdown";
import { getScheduleText, getRecordUpdate, getBatchEmailText } from "helpers/AutomationHelper";
import SendTestEmailButton from "./SendTestEmailButton";
import { UpdateRecordNoteType } from "helpers/enum";
import { validateNodeAndResetBorder } from "components/Rappid/RappidCanvasFunctions";
import {
  SenderMode,
  SpecificUserSourceText,
  getSenderMode,
  buildSpecificUserOptions,
  findSelectedSpecificUser,
  resolveLoggedInSpecificUser,
  specificUserFallbackSender,
  specificUserSenderState,
  customSenderState,
  getOwnerSenderLabel,
  getOwnerSenderDescription,
  getSpecificUserSenderEmail,
  getSpecificUserLocalPart
} from "./helpers/EmailSenderHelper";

// ROI-234: a step with a saved schedule or batching opens Scheduling & Delivery expanded.
export const hasSchedulingOrBatching = (formEmail) => !!(formEmail?.isSpecificTime || formEmail?.isBatchingSend);

const EmailForm = observer(forwardRef((props, ref) => {
  const { AutomationType, AutomationTypeText, EmailSenderOwnerTypeCandidate, EmailSenderOwnerTypeClient, TriggerType, EmailCategories, EmailCategory, EmailSources, EmailSource, AutomationSpecificDays, UpdateRecordType, UpdateRecordTypeClient, UpdateRecordOwnerType } = Enum;
  const { EmailSenderDefault } = Constants;
  const { formEmail, isCopiedTemplate, listSenderDomain, automationType, triggerList, triggerType, automationId, automationState, automationEmailAtsUsers, automationEmailUserIds } = automationStore;
  const { emailsList, emailTemplatesList } = emailStore;
  const { atsCategorisedFieldsForMergeTags, isJobScopeDisabled } = atsStore;
  const { candidateOwners } = candidateOwnerSettingStore;
  const isSenderEmailRequired = !formEmail.isFromOwner && !formEmail.isFromAtsUser && !formEmail.isFromUser;
  const isValidEmail = !isSenderEmailRequired || Validator.isValidEmailAutomation(formEmail.senderEmail);
  const disableButton = !isValidEmail ||
    formEmail.emailCampaignId === 0 ||
    formEmail.subject === "" ||
    formEmail.senderDomain === "" ||
    ((formEmail.senderEmail === "" || formEmail.senderName === "") &&
      !formEmail.isFromOwner && !formEmail.isFromAtsUser && !formEmail.isFromUser) ||
    (formEmail.isFromAtsUser && !formEmail.atsUserId) ||
    (formEmail.isFromUser && !formEmail.userId) ||
    (formEmail.isSpecificTime && (!formEmail.startHour || formEmail.specificDays?.length === 0));
  const disableSendTestButton = !isValidEmail ||
    formEmail.emailCampaignId === 0 ||
    formEmail.subject === "" ||
    formEmail.senderDomain === "" ||
    formEmail.senderEmail === "" || formEmail.senderName === "" ||
    (formEmail.isFromAtsUser && !formEmail.atsUserId) ||
    (formEmail.isFromUser && !formEmail.userId);
  const optionsType = automationType === AutomationType.Candidate ? AutomationTypeText.Candidate : AutomationTypeText.Contact;
  const emailOptions = emailsList?.filter(x => x.category && x.category.toLowerCase().includes(optionsType.toLowerCase())).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  const emailTemplateOptions = emailTemplatesList?.filter(x => x.category && x.category.toLowerCase().includes(optionsType.toLowerCase())).sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
  const roleEmailSources = userStore.user.roleId === Enum.RoleName.Recruiter ? EmailSources.filter(x => x.id !== EmailSource.Scratch) : EmailSources;
  const isPlacementOrJobBasedTrigger = [TriggerType.PlacementBased, TriggerType.JobBased].includes(triggerType);
  const ownerType = automationType === AutomationType.Candidate ? EmailSenderOwnerTypeCandidate : EmailSenderOwnerTypeClient;
  const ownerDefaultValue = isPlacementOrJobBasedTrigger ? ownerType[1] : ownerType[0];
  const optionsUpdateRecord = automationType === AutomationType.Candidate ? UpdateRecordType : UpdateRecordTypeClient;
  const updateRecordDefaultValue = isPlacementOrJobBasedTrigger ? optionsUpdateRecord[1] : optionsUpdateRecord[0];
  const [updateRecordOption, setUpdateRecordOption] = useState(optionsUpdateRecord);
  const subjectEmailRef = useRef();
  const previewTextRef = useRef();
  const sendTestEmailRef = useRef(null);
  const isEmailBatchingEnabled = featureStore.getFeatureFlag(Constants.FeatureFlags.EMAIL_BATCHING_SEND);
  const batchSizeOptions = [5000, 10000, 15000, 20000].map(x => ({ label: x.toString(), value: x }));
  const batchSizeDefaultValue = batchSizeOptions[0];
  const delayInHoursOptions = [7,6,5,4,3,2,1,0].map(x => ({
    label: x === 0 ? 'Select hours' : `${x} days`,
    value: x * 24
  }));
  const delayInHoursDefaultValue = delayInHoursOptions[0];
  const selectHoursOptions = Array.from({ length: 23 }, (_, i) => ({
    label: `${i + 1} ${i + 1 === 1 ? 'hour' : 'hours'}`,
    value: i + 1
  }));
  const selectHoursDefaultValue = selectHoursOptions[0];
  const isDraft = automationState === Enum.AutomationStateType.Draft;
  const [isOwnerMenuOpen, setIsOwnerMenuOpen] = useState(false);
  // ROI-234: Scheduling & Delivery is collapsed by default, but opens on a step that already has a
  // schedule or batching saved so the active option is visible. Open/closed is view state only.
  const [isSchedulingDeliveryOpen, setIsSchedulingDeliveryOpen] = useState(() => hasSchedulingOrBatching(automationStore.formEmail));
  const senderMode = getSenderMode(formEmail);
  const specificUserOptions = buildSpecificUserOptions(automationEmailAtsUsers, automationEmailUserIds, formEmail.isFromUser ? formEmail.userId : null);
  const selectedSpecificUser = findSelectedSpecificUser(formEmail, specificUserOptions);
  const loggedInSpecificUser = resolveLoggedInSpecificUser(userStore.user, specificUserOptions);
  const ownerSenderType = formEmail.isFromOwner && formEmail.ownerType ? formEmail.ownerType : ownerDefaultValue.value;
  const ownerSenderLabel = getOwnerSenderLabel(ownerSenderType, automationType, triggerType);
  // The address a "Specific user" email is really sent from, used for the From line and the test email.
  const specificUserSenderEmail = getSpecificUserSenderEmail(selectedSpecificUser, formEmail.senderDomain);
  const testEmailSender = senderMode === SenderMode.SpecificUser && selectedSpecificUser
    ? { senderName: selectedSpecificUser.fullName, senderEmail: getSpecificUserLocalPart(selectedSpecificUser) }
    : null;

  const isShowPlacementAdditionalNotes = () => {
    if (automationType === Enum.AutomationType.Candidate) {
      return triggerList.some(item => [
        Enum.TriggerList.CandidatePlacementStartDate,
        Enum.TriggerList.CandidatePlacementEndDate,
        Enum.TriggerList.CandidatePlacementCreatedDate,
        Enum.TriggerList.PlacementJobApplicationReceived
      ].includes(item.firstColumn.id));
    } else {
      return triggerList.some(item => [
        Enum.TriggerListClient.ClientPlacementCreatedDate,
        Enum.TriggerListClient.ClientPlacementStartDate,
        Enum.TriggerListClient.ClientPlacementEndDate
      ].includes(item.firstColumn.id));
    }
  }

  const isShowApplicationAdditionalNotes = () => {
    const isBullhornProvider = atsStore.providerName === Enum.AtsProvider.Bullhorn;
    
    if (isBullhornProvider) {
      return false;
    }

    if (automationType === Enum.AutomationType.Candidate) {
      return triggerList.some(item => [
        Enum.TriggerList.CandidatePlacementStartDate,
        Enum.TriggerList.CandidatePlacementEndDate,
        Enum.TriggerList.CandidatePlacementCreatedDate,
        Enum.TriggerList.ApplicationStatus,
        Enum.TriggerList.JobStatus,
        Enum.TriggerList.JobJobApplicationReceived,
        Enum.TriggerList.PlacementJobApplicationReceived,
        Enum.TriggerList.InterviewDate
      ].includes(item.firstColumn.id));
    } else {
      return triggerList.some(item => [].includes(item.firstColumn.id));
    }
  }

  const isShowJobAdditionalNotes = () => {
    if (automationType === Enum.AutomationType.Candidate) {
      return triggerList.some(item => [
        Enum.TriggerList.CandidatePlacementStartDate,
        Enum.TriggerList.CandidatePlacementEndDate,
        Enum.TriggerList.CandidatePlacementCreatedDate,
        Enum.TriggerList.ApplicationStatus,
        Enum.TriggerList.JobStatus,
        Enum.TriggerList.JobJobApplicationReceived,
        Enum.TriggerList.PlacementJobApplicationReceived,
        Enum.TriggerList.InterviewDate
      ].includes(item.firstColumn.id));
    } else {
      return triggerList.some(item => [
        Enum.TriggerListClient.ClientPlacementCreatedDate,
        Enum.TriggerListClient.ClientPlacementStartDate,
        Enum.TriggerListClient.ClientPlacementEndDate,
        Enum.TriggerListClient.JobStatus,
        Enum.TriggerListClient.ClientJobInterviewDate
      ].includes(item.firstColumn.id));
    }
  }

  // Reads the store, not the render closure: the init effect calls it after getAtsUsers() resolves.
  const getLoggedInSenderState = (currentFormEmail) => {
    const options = buildSpecificUserOptions(automationStore.automationEmailAtsUsers, automationStore.automationEmailUserIds);
    return getSpecificUserSenderState(resolveLoggedInSpecificUser(userStore.user, options), currentFormEmail, null);
  };

  // A "Specific user" pick: the sender fields, plus that person as the run-time fallback while the
  // saved name and email are still placeholders (PR 10579 review).
  const getSpecificUserSenderState = (option, currentFormEmail, previousOption) => {
    const isBullhorn = atsStore.providerName === Enum.AtsProvider.Bullhorn;
    return {
      ...specificUserSenderState(option, { isBullhorn, previous: currentFormEmail }),
      ...specificUserFallbackSender(option, currentFormEmail, {
        placeholder: EmailSenderDefault,
        isValidEmailName: Validator.isValidEmailAutomation,
        previousOption
      })
    };
  };

  useEffect(() => {
    const targetElement = rappidStore.activeElement;
    const initialFormEmail = { ...formEmail };
    const isEditingExistingEmail = targetElement?.attributes?.roiNodeType === RappidNodeType.Email;
    // Read before the loads: an existing node is opened with isSidebarFormUpdate set (InitRappid).
    const isNewStep = !automationStore.isSidebarFormUpdate;
    Promise.all([
      atsStore.getCompanyInfo(),
      atsStore.getAtsField(),
      atsStore.getAtsCategorisedFields(automationType),
      candidateOwnerSettingStore.getCandidateOwners(),
      emailStore.getEmailTemplates(),
      featureStore.fetchFeatureFlag(Constants.FeatureFlags.EMAIL_BATCHING_SEND),
      emailStore.getEmails(),
      automationStore.getAtsUsers()
    ]).then(() => {
      const isStaleNode = rappidStore.activeElement !== targetElement;
      const isBullhornProvider = atsStore.providerName === Enum.AtsProvider.Bullhorn;

      const initialOptions = isBullhornProvider
        ? optionsUpdateRecord.filter(item => item.value !== Enum.UpdateRecordOwnerType.NoUser && item.value !== Enum.UpdateRecordOwnerType.PlacementRecruiter)
        : optionsUpdateRecord;
      if (!isStaleNode) setUpdateRecordOption(initialOptions);

      // For Bullhorn, convert NoUser to AdminUser since NoUser is not available
      if (!isStaleNode && isBullhornProvider && automationStore.formEmail.updateRecordType === UpdateRecordOwnerType.NoUser) {
        automationStore.setFormEmail({ ...automationStore.formEmail, updateRecordType: UpdateRecordOwnerType.AdminUser }, true);
      }

      const cloneFormEmail = isStaleNode ? { ...initialFormEmail } : { ...automationStore.formEmail };
      if (isEditingExistingEmail && isCopiedTemplate === true && automationId !== null && typeof cloneFormEmail.isUpdatedFromCopiedTemplate === "undefined") {

        cloneFormEmail.emailCategoryId = 0;
        cloneFormEmail.emailCategoryName = "";
        cloneFormEmail.emailSourceId = 0;
        cloneFormEmail.emailSourceName = "";
        cloneFormEmail.emailNewTemplateName = "";
        cloneFormEmail.emailCampaignId = 0;
        cloneFormEmail.emailCampaignName = "";
        cloneFormEmail.oriEmailCampaignId = 0;
        cloneFormEmail.oriEmailCampaignName = "";
        cloneFormEmail.senderName = EmailSenderDefault.Name;
        cloneFormEmail.senderEmail = EmailSenderDefault.Email;
        cloneFormEmail.senderDomain = EmailSenderDefault.Domain;
        cloneFormEmail.atsUserId = null;

        // ROI-235: a "Specific user" copied from a template points at someone in the template's
        // company, so it is re-pointed at the person building the automation.
        if (getSenderMode(cloneFormEmail) === SenderMode.SpecificUser) {
          Object.assign(cloneFormEmail, getLoggedInSenderState(cloneFormEmail));
        }

        if (!isStaleNode) {
          automationStore.setFormEmail(cloneFormEmail, true);
          automationStore.setIsAutomationFormDirty(true);
          automationStore.setAutomationOnClear(true);
        }
        EditRappidNode(RappidNodeType.Email, cloneFormEmail.emailCampaignName, "", "", "", cloneFormEmail, false, true, targetElement);
      }
      else if (!isStaleNode && typeof formEmail.emailCategoryId === "undefined") {
        // Patch #7584 #7585
        const cloneFormEmail = { ...automationStore.formEmail };
        cloneFormEmail.emailCategoryId = EmailCategory.Existing;
        cloneFormEmail.emailCategoryName = EmailCategories.find(x => x.id === EmailCategory.Existing).name;
        if (typeof cloneFormEmail.emailSourceId === "undefined") {
          cloneFormEmail.emailSourceId = 0;
          cloneFormEmail.emailSourceName = "";
        }
        automationStore.setFormEmail(cloneFormEmail, true);
      }

      if (isStaleNode) return;

      if (formEmail.emailCategoryId === EmailCategory.New)
      {
        automationStore.setIsOpenNewTab(true);
      }

      if ((triggerList.length > 0 && !isPlacementOrJobBasedTrigger) || triggerList.length === 0) {
        //reset owner when trigger list is null or trigger type is not placement based,
        //unless the selected owner doesn't depend on job/placement context (e.g. most recent note owner)
        if (automationStore.formEmail.ownerType !== UpdateRecordOwnerType.MostRecentNoteOwner) {
          automationStore.setFormEmail({ ...automationStore.formEmail, ownerType: ownerDefaultValue.value }, true);
        }
        setUpdateRecordOption(getFilteredUpdateRecordOptions(UpdateRecordType, [UpdateRecordOwnerType.JobOwner]));
      }

      //add new properties for handle old data email and reset specific time #8293
      if (typeof formEmail.isSpecificTime === "undefined" || !formEmail.isSpecificTime) {
        let data = { ...automationStore.formEmail };
        data.isSpecificTime = false;
        data.specificDays = [AutomationSpecificDays.Monday, AutomationSpecificDays.Tuesday, AutomationSpecificDays.Wednesday, AutomationSpecificDays.Thursday, AutomationSpecificDays.Friday];
        data.startHour = null;
        data.convertedStartHour = null;
        automationStore.setFormEmail(data, true);
      }

      //add new properties for handle old data email and reset email batch #8293
      if (typeof formEmail.isBatchingSend === "undefined" || !formEmail.isBatchingSend) {
        let data = { ...automationStore.formEmail };
        data.isBatchingSend = false;
        data.batchSize = 0;
        data.delayInHours = 0;
        data.selectedDelayInDays = null;
        data.selectedDelayInHours = null;
        automationStore.setFormEmail(data, true);
      }

      //add new properties for handle old data #8292
      if (typeof formEmail.isUpdateRecord === "undefined") {
        let data = { ...automationStore.formEmail };
        data.isUpdateRecord = false;
        data.updateRecordType = 1;
        automationStore.setFormEmail(data, true);
      }

      //add new properties for handle old data email send from ats user
      if (typeof formEmail.isFromAtsUser === "undefined") {
        let data = { ...automationStore.formEmail };
        data.isFromAtsUser = false;
        data.atsUserId = null;
        automationStore.setFormEmail(data, true);
      }

      //add new properties for handle old data email send from ROI user
      if (typeof formEmail.isFromUser === "undefined") {
        let data = { ...automationStore.formEmail };
        data.isFromUser = false;
        data.userId = null;
        automationStore.setFormEmail(data, true);
      }

      // ROI-235: a new step on a candidate or contact trigger sends from the logged-in user. Job and
      // placement triggers keep the owner default resetFormEmail already set (owner type 2, run as
      // the job owner or the placement owner). Existing steps are never changed here.
      if (isNewStep && !isPlacementOrJobBasedTrigger) {
        automationStore.setFormEmail({ ...automationStore.formEmail, ...getLoggedInSenderState(automationStore.formEmail) }, true);
      }

      if (!isPlacementOrJobBasedTrigger || triggerList.length === 0) {
        if (!automationStore.formEmail.updateRecordType || [optionsUpdateRecord[1].value, optionsUpdateRecord[4].value].includes(automationStore.formEmail.updateRecordType))
          automationStore.setFormEmail({ ...automationStore.formEmail, updateRecordType: updateRecordDefaultValue.value }, true);

        setUpdateRecordOption(getFilteredUpdateRecordOptions(optionsUpdateRecord, [optionsUpdateRecord[1].value, UpdateRecordOwnerType.PlacementRecruiter]));
      }

      if (!isPlacementOrJobBasedTrigger && automationStore.formEmail.isUpdateRecord) {
        if (automationStore.formEmail.additionalNoteType ?? false !== Enum.UpdateRecordNoteType.CandidateNote) {
          automationStore.setFormEmail({ ...automationStore.formEmail, additionalNoteType: Enum.UpdateRecordNoteType.CandidateNote });
        }
      }

      if (isPlacementOrJobBasedTrigger) {
        const filters = new Set();

        if (triggerType !== TriggerType.PlacementBased) {
          filters.add(UpdateRecordOwnerType.PlacementRecruiter);

          // convert to owner default value when selected owner is Placement Recruiter
          const ownerType = formEmail.updateRecordType === UpdateRecordOwnerType.PlacementRecruiter
            ? ownerDefaultValue.value
            : formEmail.updateRecordType;

          const updateRecordType = formEmail.updateRecordType === UpdateRecordOwnerType.PlacementRecruiter
            ? updateRecordDefaultValue.value
            : formEmail.updateRecordType;

          // convert to default note when additional notes is placement
          const additionalNoteType = formEmail.additionalNoteType === Enum.UpdateRecordNoteType.PlacementNote
            ? Enum.UpdateRecordNoteType.JobNote
            : formEmail.additionalNoteType;

          automationStore.setFormEmail({
            ...automationStore.formEmail,
            updateRecordType: updateRecordType,
            ownerType: ownerType,
            isUpdateRecord: formEmail.isUpdateRecord,
            isUpdateAdditionalNotes: formEmail.isUpdateRecord,
            additionalNoteType: additionalNoteType
          }, true);
        }

        if (automationStore.formEmail.additionalNoteType ?? false !== Enum.UpdateRecordNoteType.CandidateNote) {
          filters.add(UpdateRecordOwnerType.NoUser);
        }

        setUpdateRecordOption(getFilteredUpdateRecordOptions(optionsUpdateRecord, Array.from(filters)));
      }
    });

    return function cleanup() {
      automationStore.resetFormEmail();
    }
    // eslint-disable-next-line
  }, []);

  console.debug("Email Form Data: ", toJS(formEmail));

  // remove whitespace from the sender email input (typed or pasted; paste fires onChange too)
  const handleRemoveSpace = (value) => value.replace(/\s/g, "");

  const redirectToEmailEditor = (event) => {
    if (!formEmail.emailCampaignId) {
      Alert.error('Select email first to continue')
    } else
      openEmailInNewTab(EmailSource.ViewEmail, formEmail.emailCampaignId);
  }

  const setExistingEmailCampaign = (event) => {
    const selectId = parseInt(event.id);
    const selectName = event.name;
    const emailCampaignData = {
      ...formEmail,
      emailCampaignId: selectId,
      emailCampaignName: selectName
    };
    automationStore.setFormEmail(emailCampaignData);
  }

  const setNewEmailCampaign = (event) => {
    const selectId = parseInt(event.id);
    const selectName = event.name;
    const emailCampaignData = {
      ...formEmail,
      oriEmailCampaignId: selectId,
      oriEmailCampaignName: selectName,
      emailCampaignId: 0,
      emailCampaignName: ""
    };
    automationStore.setFormEmail(emailCampaignData);
  }

  const resetEmailForm = () => {
    automationStore.setFormEmail({
      ...formEmail,
      emailCategoryId: 0,
      emailCategoryName: "",
      emailSourceId: 0,
      emailSourceName: "",
      emailCampaignId: 0,
      emailCampaignName: "",
      subject: "",
    });
  }

  const openNewTabSwitcher = (emailSourceId, selectedId) => {
    // eslint-disable-next-line default-case
    switch (emailSourceId) {
      case EmailSource.CopyExisting:
        return window.open(`/admin/emails/editor/${selectedId}?fromAutomation=1&fromScratch=1&sourceId=${selectedId}&candidateAutomation=${automationStore.automationType === AutomationType.Candidate ? 1 : 0}`, "_blank");
      case EmailSource.Template:
        return window.open(`/admin/emails/editor?tid=${selectedId}&fromAutomation=1&fromScratch=1&sourceTid=${selectedId}&candidateAutomation=${automationStore.automationType === AutomationType.Candidate ? 1 : 0}`, "_blank");
      case EmailSource.Scratch:
        return window.open(`/admin/emails/editor?fromAutomation=1&fromScratch=1&candidateAutomation=${automationStore.automationType === AutomationType.Candidate ? 1 : 0}`, "_blank");
      case EmailSource.ViewEmail:
        return window.open(`/admin/emails/editor/${selectedId}?fromAutomation=1&candidateAutomation=${automationStore.automationType === AutomationType.Candidate ? 1 : 0}`, '_blank');
    }
  }

  const openEmailInNewTab = (emailSourceId, selectedId) => {
    const newTab = openNewTabSwitcher(emailSourceId, selectedId);

    newTab.addEventListener("message", response => {
      const { data } = response;

      // Handle different types of closures
      switch(data.type) {
        case 'CLEAN_CLOSE':
          // Do nothing on clean close
          return;

        case 'WINDOW_CLOSE':
          if (data.hasUnsavedChanges) {
            resetEmailForm();
          }
          return;

        case 'EXPLICIT_CANCEL':
          resetEmailForm();
          emailStore.setIsCopyToExisting(false);
          emailStore.setIsFromCopyToExisting(false);
          emailStore.setIsUseEmailTemplate(false);
          automationStore.setIsOpenNewTab(false);
          return;

        // Handle save operations as before
        default:
          if (data.isSave) {
            if (!data.isFormCopyToExisting) {
              if (emailSourceId === EmailSource.ViewEmail) {
                emailStore.getEmails().then(() => {
                  automationStore.setFormEmail({
                    ...formEmail,
                    emailCampaignId: data.emailCampaignId,
                    emailCampaignName: data.emailCampaignName
                  });
                });
              } else {
                automationStore.setFormEmail({
                  ...formEmail,
                  emailSourceId: emailSourceId,
                  oriEmailCampaignId: selectedId,
                  emailCampaignId: data.emailCampaignId,
                  emailCampaignName: data.emailCampaignName
                });
              }
              emailStore.setIsCopyToExisting(false);
              emailStore.setIsUseEmailTemplate(false);
            } else {
              resetEmailForm();
              emailStore.setIsCopyToExisting(false);
              emailStore.setIsFromCopyToExisting(false);
              emailStore.setIsUseEmailTemplate(false);
              automationStore.setIsOpenNewTab(false);
            }
          }
      }
    });
  };

  const renderDaysComponent = (ListDays) => {
    if (automationStore.formEmail.specificDays === undefined)
      automationStore.formEmail.specificDays = [AutomationSpecificDays.Monday, AutomationSpecificDays.Tuesday, AutomationSpecificDays.Wednesday, AutomationSpecificDays.Thursday, AutomationSpecificDays.Friday];

    // ROI-234: one row of compact day buttons. Still multi-select, with the same specificDays and
    // timeVariableDays updates the old checkboxes made.
    const daysComponent = ListDays.map((item) => {
      const isChecked = automationStore.formEmail.specificDays.some(x => x === item.value);

      return (
        <ScheduleDayButton
          key={item.value}
          type="button"
          id={`email-form-time-variable-${item.day}`}
          className="automation-time-variable-day-button"
          aria-pressed={isChecked}
          isSelected={isChecked}
          disabled={!isDraft}
          onClick={() => {
            let data = { ...formEmail };
            data.specificDays = [...formEmail.specificDays];
            if (!isChecked) {
              data.specificDays = [...data.specificDays, item.value];
            } else {
              const removeValue = data.specificDays.indexOf(item.value);
              data.specificDays.splice(removeValue, 1);
            }
            data.timeVariableDays = data.specificDays.toString();
            automationStore.setFormEmail(data);
          }}
        >
          {item.day}
        </ScheduleDayButton>
      );
    });
    return daysComponent;
  };

  // The off/on values each option has always written, kept as they were.
  const getScheduleState = (isOn) => {
    if (isOn) {
      const startHour = new Date(moment().set({ hour: 8, minute: 0, second: 0, millisecond: 0 }));
      return { isSpecificTime: true, startHour, convertedStartHour: automationStore.convertStepTimeToString(startHour) };
    }
    return { isSpecificTime: false, startHour: null, convertedStartHour: null };
  };

  const getBatchingState = (isOn) => {
    if (isOn) {
      return {
        isBatchingSend: true,
        batchSize: batchSizeDefaultValue.value,
        delayInHours: delayInHoursDefaultValue.value,
        selectedDelayInDays: delayInHoursDefaultValue.value,
        selectedDelayInHours: null
      };
    }
    return { isBatchingSend: false, batchSize: 0, delayInHours: 0, selectedDelayInDays: null, selectedDelayInHours: null };
  };

  // ROI-234 single-select: picking one option turns the other off; picking the active one again
  // clears it. An older step saved with both on keeps both until the user changes one of them,
  // which then clears only the option clicked.
  const handleScheduleOptionChange = () => {
    const isTurningOn = !formEmail.isSpecificTime;
    automationStore.setFormEmail({
      ...formEmail,
      ...getScheduleState(isTurningOn),
      ...(isTurningOn && formEmail.isBatchingSend ? getBatchingState(false) : {})
    });
  };

  const handleBatchingOptionChange = () => {
    const isTurningOn = !formEmail.isBatchingSend;
    automationStore.setFormEmail({
      ...formEmail,
      ...getBatchingState(isTurningOn),
      ...(isTurningOn && formEmail.isSpecificTime ? getScheduleState(false) : {})
    });
  };

  const handleAutomationSubjectEmailTokenSelected = (token) => {
    const automationTextToken = ` {{${token}}}`;

    if (subjectEmailRef.current) {
      subjectEmailRef.current.value = formEmail.subject + automationTextToken;
      automationStore.setFormEmail({ ...formEmail, subject: subjectEmailRef.current.value });
    }
  }

  const handleAutomationPreviewTextTokenSelected = (token) => {
    const automationTextToken = ` {{${token}}}`;

    if (previewTextRef.current) {
      previewTextRef.current.value = (formEmail.previewText || '') + automationTextToken;
      automationStore.setFormEmail({ ...formEmail, previewText: previewTextRef.current.value });
    }
  }

  const isShowAdditionalNotes = () => !isJobScopeDisabled && isShowJobAdditionalNotes();

  const getFilteredOwnerOptions = () => {
    const isBullhornProvider = atsStore.providerName === Enum.AtsProvider.Bullhorn;
    const baseFilters = [UpdateRecordOwnerType.AdminUser];

    baseFilters.push(UpdateRecordOwnerType.NoUser);

    // "Job owner" and "Placement recruiter" only make sense when there's a job/placement context;
    // "Most recent note owner" has no such dependency, so it stays available for any trigger type.
    if (!isPlacementOrJobBasedTrigger) {
      baseFilters.push(UpdateRecordOwnerType.JobOwner);
    }

    if (triggerType !== TriggerType.PlacementBased) {
      baseFilters.push(UpdateRecordOwnerType.PlacementRecruiter);
    }

    // Hide "No user" and "Placement recruiter" options when for Bullhorn provider
    if (isBullhornProvider) {
      baseFilters.push(UpdateRecordOwnerType.NoUser);
      baseFilters.push(UpdateRecordOwnerType.PlacementRecruiter);
    }

    return ownerType.filter(item => !baseFilters.includes(item.value));
  }

  const getFilteredUpdateRecordOptions = (baseOptions, additionalFilters = []) => {
    const isBullhornProvider = atsStore.providerName === Enum.AtsProvider.Bullhorn;
    const filters = [...additionalFilters];

    // Hide "No user" and "Placement recruiter" options when for Bullhorn provider
    if (isBullhornProvider) {
      filters.push(UpdateRecordOwnerType.NoUser);
      filters.push(UpdateRecordOwnerType.PlacementRecruiter);
    }

    return baseOptions.filter(item => !filters.includes(item.value));
  }

  // ROI-235 "Send from" handlers. The custom sender name and email stay in the step as the run-time
  // fallback for the other modes, so leaving "Custom sender" empty puts the defaults back.
  const withSenderFallback = (data) => ({
    ...data,
    senderName: data.senderName || EmailSenderDefault.Name,
    senderEmail: data.senderEmail || EmailSenderDefault.Email
  });

  const handleSelectSpecificUserMode = () => {
    if (!isDraft || senderMode === SenderMode.SpecificUser) return;
    const base = withSenderFallback(formEmail);
    automationStore.setFormEmail({ ...base, ...getLoggedInSenderState(base) });
  };

  const handleSelectSpecificUser = (option) => {
    automationStore.setFormEmail({ ...formEmail, ...getSpecificUserSenderState(option, formEmail, selectedSpecificUser) });
  };

  const handleSelectOwner = (newOwnerType) => {
    if (!isDraft) return;
    setIsOwnerMenuOpen(false);
    automationStore.setFormEmail(withSenderFallback({
      ...formEmail,
      isFromOwner: true,
      ownerType: newOwnerType,
      isFromAtsUser: false,
      atsUserId: null,
      isFromUser: false,
      userId: null,
      // "Most recent note owner" isn't a valid UpdateRecordType on the BE (ROI-BE PR 10296 only
      // extended OwnerType), so keep the record-update owner at the default instead of mirroring 7.
      updateRecordType: newOwnerType === UpdateRecordOwnerType.MostRecentNoteOwner ? updateRecordDefaultValue.value : newOwnerType
    }));
  };

  const handleSelectCustomSender = () => {
    if (!isDraft || senderMode === SenderMode.Custom) return;
    // The "test" placeholders are not a sender anyone chose: clear them so the fields ask for one.
    const isPlaceholderSender = formEmail.senderName === EmailSenderDefault.Name && formEmail.senderEmail === EmailSenderDefault.Email;
    // "Database user" is only offered while a database user sends, so leaving one resets the note
    // author to the step's default, as switching to an owner or a Roi-AI user already does.
    const isBullhorn = atsStore.providerName === Enum.AtsProvider.Bullhorn;
    const withoutDatabaseUser = formEmail.updateRecordType === UpdateRecordOwnerType.DatabaseUser
      ? {
        updateRecordType: isPlacementOrJobBasedTrigger
          ? updateRecordDefaultValue.value
          : (isBullhorn ? UpdateRecordOwnerType.AdminUser : UpdateRecordOwnerType.NoUser)
      }
      : {};
    automationStore.setFormEmail({
      ...formEmail,
      ...customSenderState(),
      ...withoutDatabaseUser,
      senderName: isPlaceholderSender ? "" : formEmail.senderName,
      senderEmail: isPlaceholderSender ? "" : formEmail.senderEmail
    });
  };

  const formatSpecificUserOption = (option, { context }) => {
    const youTag = loggedInSpecificUser && option.key === loggedInSpecificUser.key ? " (you)" : "";
    if (context === "value") return `${option.fullName}${youTag}`;
    return (
      <div className="d-flex flex-column" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        <span style={{ fontSize: '14px', fontWeight: 500 }}>{option.fullName}{youTag}</span>
        <span style={{ fontSize: '12px', opacity: 0.7 }}>{option.email} · {SpecificUserSourceText[option.kind]}</span>
      </div>
    );
  };

  const filterSpecificUserOption = (candidate, input) => {
    if (!input) return true;
    const query = input.trim().toLowerCase();
    return candidate.data.fullName.toLowerCase().includes(query) || candidate.data.email.toLowerCase().includes(query);
  };

  const handleCheckboxChecked = (noteType) => {
    const newUpdateRecordType = noteType === UpdateRecordNoteType.CandidateNote
      ? formEmail.updateRecordType
      : formEmail.updateRecordType === UpdateRecordOwnerType.NoUser ? UpdateRecordOwnerType.AdminUser : formEmail.updateRecordType;

    const filters = new Set();

    if (triggerType !== TriggerType.PlacementBased)
      filters.add(UpdateRecordOwnerType.PlacementRecruiter);

    if (noteType !== UpdateRecordNoteType.CandidateNote)
      filters.add(UpdateRecordOwnerType.NoUser);

    automationStore.setFormEmail({
      ...formEmail,
      isUpdateRecord: formEmail.isUpdateRecord,
      isUpdateAdditionalNotes: formEmail.isUpdateRecord,
      additionalNoteType: noteType,
      updateRecordType: newUpdateRecordType
    });

    setUpdateRecordOption(getFilteredUpdateRecordOptions(optionsUpdateRecord, Array.from(filters)));
  }

  useImperativeHandle(ref, () => ({
    isModalOpen: () => sendTestEmailRef.current?.isModalOpen() || false,
    openModal: () => sendTestEmailRef.current?.openModal(),
    closeModal: () => sendTestEmailRef.current?.closeModal(),
  }));

  return (
    <>
      <HeaderForm
        automationHeaderId="form-step-email"
        automationHeaderImgSrc={stepEmailIcon}
        automationHeaderImgAlt="icon-email"
        automationHeaderTextStep={RappidText.EmailStep}
      />
      <div style={{ 
        color: 'rgba(255, 255, 255, 0.85)', 
        fontSize: '11px', 
        fontWeight: 600, 
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        marginBottom: '12px',
        marginTop: '8px',
        paddingBottom: '8px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        EMAIL SOURCE
      </div>
      <Row className="ml-0 mr-0">
        <SidebarFormSelect
          className="automation-email-source col-md-12 px-0 mb-2"
          id="automation-email-category"
          name="automation-email-category"
          classNamePrefix="automation-dropdown"
          placeholder="- Choose a campaign source -"
          isDisabled={!isDraft}
          onChange={e => {
            if (formEmail.emailCategoryId !== e.id) {
              const data = {
                ...formEmail,
                emailCategoryId: e.id,
                emailCategoryName: e.name,
                emailSourceId: 0,
                emailSourceName: "- Select source -",
                emailCampaignId: 0,
                emailCampaignName: ""
              };
              automationStore.setFormEmail(data);
              automationStore.setIsOpenNewTab(false);
            }
          }}
          options={EmailCategories}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          value={EmailCategories.find(x => x.id === formEmail.emailCategoryId) || 0}
        />
      </Row>
      {formEmail.emailCategoryId === EmailCategory.New && (
        <Row className="ml-0 mr-0">
          <SidebarFormSelect
            className="automation-email-template col-md-12 px-0 mb-2"
            id="automation-email-source"
            name="automation-email-source"
            classNamePrefix="automation-dropdown"
            placeholder="- Select source -"
            isDisabled={!isDraft}
            onChange={e => {
              if (formEmail.emailSourceId !== e.id) {
                const selectedId = parseInt(e.id);
                const selectedName = e.name;
                const emailCampaignData = {
                  ...formEmail,
                  emailSourceId: selectedId,
                  emailSourceName: selectedName,
                  emailCampaignName: ""
                };
                automationStore.setFormEmail(emailCampaignData);

                if (e.id === EmailSource.CopyExisting) {
                  automationStore.setIsOpenNewTab(true);
                } else if (e.id === EmailSource.Template) {
                  automationStore.setIsOpenNewTab(true);
                } else {
                  automationStore.setIsOpenNewTab(true);
                  openEmailInNewTab(selectedId, null);
                }
              }
            }}
            options={roleEmailSources}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            value={roleEmailSources.find(x => x.id === formEmail.emailSourceId) || 0}
          />
        </Row>
      )}
      {(formEmail.emailCategoryId === EmailCategory.Existing || formEmail.emailSourceId === EmailSource.CopyExisting || formEmail.emailSourceId === EmailSource.Template || formEmail.emailSourceId === EmailSource.Scratch) &&
        <>
          {(formEmail.emailCategoryId === EmailCategory.Existing || formEmail.emailSourceId === EmailSource.CopyExisting) && (
            <Row className="ml-0 mr-0">
              <SidebarFormSelect
                className="automation-email-template col-md-12 px-0 mb-2"
                id="automation-email-source"
                name="automation-email-source"
                classNamePrefix="automation-dropdown"
                placeholder="- Select email -"
                isDisabled={!isDraft}
                onChange={e => {
                  if (formEmail.emailSourceId === EmailSource.CopyExisting) {
                    setNewEmailCampaign(e)
                    if (automationStore.isOpenNewTab) {
                      openEmailInNewTab(formEmail.emailSourceId, e.id);
                    }
                  }
                  else {
                    setExistingEmailCampaign(e)
                  }
                }}
                options={emailOptions}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={formEmail.emailCategoryId === EmailCategory.Existing ? emailOptions.find(x => x.id === formEmail.emailCampaignId) : emailOptions.find(x => x.id === formEmail.oriEmailCampaignId)}
              />
            </Row>
          )}
          {formEmail.emailSourceId === EmailSource.Template && (
            <Row className="ml-0 mr-0">
              <SidebarFormSelect
                className="automation-email-template col-md-12 px-0 mb-2"
                id="automation-email-source"
                name="automation-email-source"
                classNamePrefix="automation-dropdown"
                placeholder="- Select template -"
                isDisabled={!isDraft}
                onChange={e => {
                  setNewEmailCampaign(e);
                  openEmailInNewTab(formEmail.emailSourceId, e.id);
                }}
                options={emailTemplateOptions}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={emailTemplateOptions.find(x => x.id === formEmail.oriEmailCampaignId)}
              />
            </Row>
          )}
          {automationStore.isOpenNewTab && (
            <Row className="ml-0 mr-0 mb-2">
              <Input
                type="text"
                id="automation-email-new-template-name"
                className="rounded-pill automation-email-input"
                placeholder="Save your email to view it here"
                disabled={!isDraft}
                value={automationStore.formEmail.emailCampaignName}
                readOnly={true}
              />
            </Row>
          )}
          <Row className="ml-0 mr-0">
            <EmailEditorButton
              id="automation-email-form-redirect-editor-button"
              className="col-md-12 mt-3 btn-roi secondary outline-white pr-5"
              disabled={!formEmail.emailCampaignId}
              onClick={redirectToEmailEditor}
            >
              View email
            </EmailEditorButton>
          </Row>
        </>
      }

      <div style={{ 
        color: 'rgba(255, 255, 255, 0.85)', 
        fontSize: '11px', 
        fontWeight: 600, 
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        marginBottom: '16px',
        marginTop: '10px',
        paddingBottom: '8px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        EMAIL CONTENT
      </div>

      <FormGroup className="step-body-text" style={{ marginBottom: '15px' }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span><b><Label className="text-white mb-0">Subject line</Label></b></span>
          <CompactMergeTagsDropdown
            atsCategorisedFieldsForMergeTags={atsCategorisedFieldsForMergeTags}
            onTagSelected={(tokenVariable) => handleAutomationSubjectEmailTokenSelected(tokenVariable)}
            isDisabled={!isDraft}
            isAutomationContact={automationType !== AutomationType.Candidate && triggerType === TriggerType.PlacementBased}
            buttonText="Insert merge tag"
          />
        </div>
        <div style={{ position: 'relative' }}>
          <Input
            ref={subjectEmailRef}
            type="textarea"
            rows='2'
            id="automation-email-subject"
            className="automation-email-input border-radius-normal"
            value={formEmail.subject}
            disabled={!isDraft}
            maxLength={256}
            placeholder="Enter the email subject..."
            onChange={e => {
              const value = e.target.value;
              if (value.length <= 256) {
                automationStore.setFormEmail({ ...formEmail, subject: value });
              }
            }}
            style={{ paddingBottom: '24px' }}
          />
          <small 
            className={`text-muted ${(formEmail.subject?.length || 0) > 240 ? 'text-warning' : ''} ${(formEmail.subject?.length || 0) >= 256 ? 'text-danger' : ''}`}
            style={{
              position: 'absolute',
              bottom: '6px',
              right: '10px',
              fontSize: '11px',
              pointerEvents: 'none'
            }}
          >
            {formEmail.subject?.length || 0}/256
          </small>
        </div>
      </FormGroup>

      <FormGroup className="step-body-text" style={{ marginBottom: '15px' }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="d-flex align-items-center">
            <b><Label id="previewTextLabel" className="text-white mb-0">Preview text (optional)</Label></b>
            <i className="fa fa-info-circle ml-2" id="previewTextInfo" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', cursor: 'help' }}></i>
          </span>
          <CompactMergeTagsDropdown
            atsCategorisedFieldsForMergeTags={atsCategorisedFieldsForMergeTags}
            onTagSelected={(tokenVariable) => handleAutomationPreviewTextTokenSelected(tokenVariable)}
            isDisabled={!isDraft}
            isAutomationContact={automationType !== AutomationType.Candidate && triggerType === TriggerType.PlacementBased}
            buttonText="Insert merge tag"
          />
        </div>
        <UncontrolledTooltip placement="bottom" target="previewTextInfo">
          This is optional, if not used, the preview text will show the first line from your email.
        </UncontrolledTooltip>
        <div style={{ position: 'relative' }}>
          <Input
            ref={previewTextRef}
            type="textarea"
            rows='2'
            id="automation-email-preview-text"
            className="automation-email-input border-radius-normal"
            value={formEmail.previewText || ''}
            disabled={!isDraft}
            maxLength={150}
            placeholder="Short description to encourage opens..."
            onChange={e => {
              const value = e.target.value;
              if (value.length <= 150) {
                automationStore.setFormEmail({ ...formEmail, previewText: value });
              }
            }}
            style={{ paddingBottom: '24px' }}
          />
          <small
            className={`text-muted ${(formEmail.previewText?.length || 0) > 130 ? 'text-warning' : ''} ${(formEmail.previewText?.length || 0) >= 150 ? 'text-danger' : ''}`}
            style={{
              position: 'absolute',
              bottom: '6px',
              right: '10px',
              fontSize: '11px',
              pointerEvents: 'none'
            }}
          >
            {formEmail.previewText?.length || 0}/150
          </small>
        </div>
      </FormGroup>

      <SenderSectionContainer>
        <div style={{ 
          color: 'rgba(255, 255, 255, 0.85)', 
          fontSize: '11px', 
          fontWeight: 600, 
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
          marginBottom: '16px',
          marginTop: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}>
          SENDER CONFIGURATION
        </div>

        <SendFromContainer>
          <SendFromLabel>
            <span id="sendFromInfo" className="d-inline-flex align-items-center" style={{ cursor: 'help' }}>
              Send from
              <i className="fa fa-info-circle ml-2" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}></i>
            </span>
          </SendFromLabel>
          <UncontrolledTooltip placement="bottom" target="sendFromInfo">
            Choose who this email comes from. You can change it at any time.
          </UncontrolledTooltip>

          <SendFromButtonGroup role="radiogroup" aria-label="Send from" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            <SenderModeButton
              type="button"
              id="sendFromSpecificUser"
              role="radio"
              aria-checked={senderMode === SenderMode.SpecificUser}
              isSelected={senderMode === SenderMode.SpecificUser}
              disabled={!isDraft}
              onClick={handleSelectSpecificUserMode}
            >
              Specific user
            </SenderModeButton>
            <UncontrolledTooltip placement="top" target="sendFromSpecificUser">
              Send every email from one person you choose.
            </UncontrolledTooltip>

            <SenderModeSplit>
              <Dropdown isOpen={isOwnerMenuOpen} toggle={() => isDraft && setIsOwnerMenuOpen(!isOwnerMenuOpen)}>
                <SenderModeButton
                  as="div"
                  id="sendFromOwner"
                  role="radio"
                  tabIndex={isDraft ? 0 : -1}
                  aria-checked={senderMode === SenderMode.Owner}
                  aria-disabled={!isDraft}
                  isSelected={senderMode === SenderMode.Owner}
                  disabled={!isDraft}
                  className="d-flex align-items-center justify-content-center"
                  onClick={() => handleSelectOwner(ownerSenderType)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelectOwner(ownerSenderType);
                    }
                  }}
                >
                  <span style={{ minWidth: 0 }}>{ownerSenderLabel}</span>
                  <DropdownToggle
                    tag="button"
                    type="button"
                    id="sendFromOwnerMenu"
                    aria-label="More sender options"
                    disabled={!isDraft}
                    className="sender-owner-toggle border-0 p-0 bg-transparent text-white"
                    onClick={e => e.stopPropagation()}
                  >
                    <SenderModeCaret><i className="fa fa-chevron-down"></i></SenderModeCaret>
                  </DropdownToggle>
                </SenderModeButton>
                <DropdownMenu right>
                  {getFilteredOwnerOptions().map(option => (
                    <DropdownItem
                      key={option.value}
                      id={`sendFromOwnerOption-${option.value}`}
                      active={senderMode === SenderMode.Owner && formEmail.ownerType === option.value}
                      onClick={() => handleSelectOwner(option.value)}
                    >
                      <span>{getOwnerSenderLabel(option.value, automationType, triggerType)}</span>
                      {senderMode === SenderMode.Owner && formEmail.ownerType === option.value && <i className="fa fa-check"></i>}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </SenderModeSplit>
            <UncontrolledTooltip placement="top" target="sendFromOwner">
              Send each email from the person linked to the record. Use the arrow to choose who.
            </UncontrolledTooltip>

            <SenderModeButton
              type="button"
              id="sendFromCustom"
              role="radio"
              aria-checked={senderMode === SenderMode.Custom}
              isSelected={senderMode === SenderMode.Custom}
              disabled={!isDraft}
              onClick={handleSelectCustomSender}
            >
              Custom sender
            </SenderModeButton>
            <UncontrolledTooltip placement="top" target="sendFromCustom">
              Type the sender name and email address yourself.
            </UncontrolledTooltip>
          </SendFromButtonGroup>

          {senderMode === SenderMode.SpecificUser && (
            <SendFromDropdownContainer>
              <Label for="automation-email-from-specific-user-select" className="text-white mb-2" style={{ fontSize: '13px', fontWeight: 500 }}>User</Label>
              <Select
                isDisabled={!isDraft}
                inputId="automation-email-from-specific-user-select"
                id="automation-email-from-specific-user"
                className="automation-email-template"
                classNamePrefix="automation-dropdown"
                placeholder="No user selected"
                menuPosition="fixed"
                menuPlacement="auto"
                menuShouldBlockScroll={true}
                onChange={handleSelectSpecificUser}
                options={specificUserOptions}
                getOptionLabel={(option) => option?.fullName}
                getOptionValue={(option) => option?.key}
                formatOptionLabel={formatSpecificUserOption}
                filterOption={filterSpecificUserOption}
                noOptionsMessage={() => "No users match your search."}
                value={selectedSpecificUser}
              />

              {listSenderDomain.length > 1 && (
                <>
                  <div className="d-flex align-items-center mt-3 mb-2">
                    <Label for="automation-email-specific-user-domain-select" className="text-white mb-0" style={{ fontSize: '13px', fontWeight: 500 }}>Sending domain</Label>
                    <i className="fa fa-info-circle ml-2" id="specificUserDomainInfo" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', cursor: 'help' }}></i>
                  </div>
                  <UncontrolledTooltip placement="bottom" target="specificUserDomainInfo">
                    The email goes out from the user's email name at this domain.
                  </UncontrolledTooltip>
                  <Select
                    inputId="automation-email-specific-user-domain-select"
                    className="automation-email-template"
                    classNamePrefix="automation-dropdown"
                    placeholder="Select domain"
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldBlockScroll={true}
                    isDisabled={!isDraft}
                    onChange={e => { automationStore.setFormEmail({ ...formEmail, senderDomain: e.label, companyDomainId: e.value }); }}
                    options={listSenderDomain}
                    getOptionLabel={(option) => option?.label}
                    getOptionValue={(option) => option?.value}
                    value={listSenderDomain.find(x => x.value === formEmail.companyDomainId)}
                  />
                </>
              )}
            </SendFromDropdownContainer>
          )}

          {senderMode === SenderMode.Custom && (
            <SendFromDropdownContainer>
              <FormGroup className="step-body-text" style={{ marginBottom: '20px' }}>
                <div className="d-flex align-items-center mb-2">
                  <b><Label id="senderNameLabel" for="automation-email-form-name" className="text-white mb-0" style={{ fontSize: '13px' }}>Sender name</Label></b>
                  <i className="fa fa-info-circle ml-2" id="senderNameInfo" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', cursor: 'help' }}></i>
                </div>
                <UncontrolledTooltip placement="bottom" target="senderNameInfo">
                  The name the recipient sees in their inbox
                </UncontrolledTooltip>
                <Input
                  type="text"
                  id="automation-email-form-name"
                  className="rounded-pill automation-email-input"
                  value={automationStore.formEmail.senderName}
                  placeholder="e.g. Talent team"
                  onChange={e => { automationStore.setFormEmail({ ...formEmail, senderName: e.target.value }) }}
                  disabled={!isDraft}
                />
              </FormGroup>

              <FormGroup className="step-body-text" style={{ marginBottom: '4px' }}>
                <div className="d-flex align-items-center mb-2">
                  <b><Label id="senderEmailLabel" for="automation-email-form-email" className="text-white mb-0" style={{ fontSize: '13px' }}>Sender email</Label></b>
                  <i className="fa fa-info-circle ml-2" id="senderEmailInfo" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', cursor: 'help' }}></i>
                </div>
                <UncontrolledTooltip placement="bottom" target="senderEmailInfo">
                  The email address recipients see and reply to
                </UncontrolledTooltip>
                <InputGroup>
                  <Input
                    type="text"
                    id="automation-email-form-email"
                    className="rounded-pill automation-email-input automation-email-input-form-email"
                    placeholder="e.g. talent"
                    value={formEmail.senderEmail}
                    onChange={e => { automationStore.setFormEmail({ ...formEmail, senderEmail: handleRemoveSpace(e.target.value) }); }}
                    disabled={!isDraft}
                  />
                  <InputGroupAddon addonType="append">
                    <Select
                      styles={SelectAutomation}
                      id="automation-email-form-sender-domain"
                      classNamePrefix="custom-input-dropdown"
                      placeholder="Select Domain"
                      menuPosition="fixed"
                      menuPlacement="auto"
                      label="Single select"
                      menuShouldBlockScroll={true}
                      isDisabled={!isDraft}
                      onChange={e => { automationStore.setFormEmail({ ...formEmail, senderDomain: e.label, companyDomainId: e.value }); }}
                      options={listSenderDomain}
                      getOptionLabel={(option) => option?.label}
                      getOptionValue={(option) => option?.value}
                      value={listSenderDomain.find(x => x.value === formEmail.companyDomainId)}
                    />
                  </InputGroupAddon>
                </InputGroup>
                {formEmail.senderEmail !== "" && !isValidEmail && (
                  <div className="text-danger small mt-1">Enter only the part before the @ (e.g. talent)</div>
                )}
              </FormGroup>
            </SendFromDropdownContainer>
          )}

          {senderMode !== SenderMode.Custom && (
            <SenderFromCard id="automation-email-sender-from" aria-live="polite">
              <SenderFromCardLabel>
                <span id="senderFromInfo" className="d-inline-flex align-items-center" style={{ cursor: 'help' }}>
                  From
                  <i className="fa fa-info-circle ml-2" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}></i>
                </span>
              </SenderFromCardLabel>
              <UncontrolledTooltip placement="bottom" target="senderFromInfo">
                {senderMode === SenderMode.SpecificUser
                  ? "Recipients see this name and address, and replies go to it."
                  : "The sender changes with each record. If an owner's email domain is not verified, your default sending domain is used."}
              </UncontrolledTooltip>
              {senderMode === SenderMode.SpecificUser && selectedSpecificUser && (
                <>
                  <SenderFromName>{selectedSpecificUser.fullName}</SenderFromName>
                  <SenderFromDetail>{specificUserSenderEmail}</SenderFromDetail>
                </>
              )}
              {senderMode === SenderMode.SpecificUser && !selectedSpecificUser && (
                <SenderFromDetail>
                  {formEmail.atsUserId || formEmail.userId
                    ? "This user is no longer available. Select another user."
                    : "No user selected. Choose who this email comes from."}
                </SenderFromDetail>
              )}
              {senderMode === SenderMode.Owner && (
                <>
                  <SenderFromName>{ownerSenderLabel}</SenderFromName>
                  <SenderFromDetail>{getOwnerSenderDescription(ownerSenderType, automationType, triggerType)}</SenderFromDetail>
                </>
              )}
            </SenderFromCard>
          )}
        </SendFromContainer>
      </SenderSectionContainer>

      <div style={{ 
        color: 'rgba(255, 255, 255, 0.85)', 
        fontSize: '11px', 
        fontWeight: 600, 
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        marginBottom: '16px',
        marginTop: '32px',
        paddingBottom: '8px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        <span id="testingSectionHeader" style={{ display: 'inline-flex', alignItems: 'center' }}>
          TESTING
          <i className="fa fa-info-circle ml-2" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', cursor: 'help' }}></i>
        </span>
      </div>
      <UncontrolledTooltip placement="bottom" target="testingSectionHeader">
        Test your setup to ensure variables and layouts look correct before activating the automation.
      </UncontrolledTooltip>

      <div style={{ marginBottom: '24px' }}>
        <SendTestEmailButton
          ref={sendTestEmailRef}
          formEmail={formEmail}
          senderOverride={testEmailSender}
          candidateOwners={candidateOwners}
          contactType={automationType}
          isDisabled={disableSendTestButton} />
      </div>

      <div style={{ 
        color: 'rgba(255, 255, 255, 0.85)', 
        fontSize: '11px', 
        fontWeight: 600, 
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        marginBottom: '16px',
        marginTop: '32px',
        paddingBottom: '8px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        DATABASE SETTINGS
      </div>

      <FormGroup className="step-body-text" style={{ marginBottom: '20px' }}>
        <Input
          type="checkbox"
          id="automation-email-update-record-input"
          className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
          checked={formEmail.isUpdateRecord}
          disabled={!isDraft}
          onChange={(e) => {
            automationStore.setFormEmail({ ...formEmail, isUpdateRecord: !formEmail.isUpdateRecord });
          }}
        />
        <Label check id="update-record-id" htmlFor={`automation-email-update-record-input`} className="mb-2">
          Update database note using the Subject Line
        </Label>
        <UncontrolledTooltip placement="bottom" target="update-record-id" style={{ backgroundColor: "white", color: "black" }}>
          Use the email subject line to update the database notes
        </UncontrolledTooltip>
        <Select
          isDisabled={!formEmail.isUpdateRecord || formEmail.isFromOwner || !isDraft}
          id="automation-email-from-update-record-type"
          className={`automation-email-template col-md-12 px-0 mb-2 ${!formEmail.isUpdateRecord && `automation-disable-dropdown`}`}
          classNamePrefix="automation-dropdown"
          placeholder="Select Update Record"
          menuPosition="fixed"
          menuPlacement="auto"
          menuShouldBlockScroll={true}
          onChange={(e) => {
            automationStore.setFormEmail({ ...formEmail, updateRecordType: e.value });
          }}
          options={formEmail.isFromAtsUser ? [...updateRecordOption, { label: "Database user", value: 6 }] : updateRecordOption}
          getOptionLabel={(option) => option?.label}
          getOptionValue={(option) => option?.value}
          value={formEmail.updateRecordType ? (formEmail.isFromAtsUser ? [...updateRecordOption, { label: "Database user", value: 6 }] : updateRecordOption).find(x => x.value === formEmail.updateRecordType) : updateRecordDefaultValue}
        />
      </FormGroup>

      {formEmail.isUpdateRecord && isShowAdditionalNotes() &&
        <>
          <FormGroup className="step-body-text">
            <span className="mb-3"><b><Label className="text-white">Add note to</Label></b></span><br />
            <Input
              type="checkbox"
              id="automation-update-to-candidate-note"
              className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
              checked={formEmail.isUpdateRecord && formEmail.additionalNoteType === Enum.UpdateRecordNoteType.CandidateNote}
              disabled={!isDraft}
              onChange={() => handleCheckboxChecked(UpdateRecordNoteType.CandidateNote)}
            />
            <Label check htmlFor={`automation-update-to-candidate-note`} className="mb-1 mr-3">
              {automationType === Enum.AutomationType.Candidate ? 'Candidate' : 'Contact'}
            </Label>
            {isShowPlacementAdditionalNotes() &&
              <>
                <Input
                  type="checkbox"
                  id="automation-update-to-placement-note"
                  className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
                  checked={formEmail.isUpdateRecord && formEmail.additionalNoteType === Enum.UpdateRecordNoteType.PlacementNote}
                  disabled={!isDraft}
                  onChange={() => handleCheckboxChecked(UpdateRecordNoteType.PlacementNote)}
                />
                <Label check htmlFor={`automation-update-to-placement-note`} className="mb-1 mr-3">
                  Placement
                </Label>
              </>
            }
            <Input
              type="checkbox"
              id="automation-update-to-job-note"
              className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
              checked={formEmail.isUpdateRecord && formEmail.additionalNoteType === Enum.UpdateRecordNoteType.JobNote}
              disabled={!isDraft}
              onChange={() => handleCheckboxChecked(UpdateRecordNoteType.JobNote)}
            />
            <Label check htmlFor={`automation-update-to-job-note`} className="mb-1 mr-3">
              Job
            </Label>
            {isShowApplicationAdditionalNotes() &&
              <>
                <Input
                  type="checkbox"
                  id="automation-update-to-application-note"
                  className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
                  checked={formEmail.isUpdateRecord && formEmail.additionalNoteType === Enum.UpdateRecordNoteType.ApplicationNote}
                  disabled={!isDraft}
                  onChange={() => handleCheckboxChecked(UpdateRecordNoteType.ApplicationNote)}
                />
                <Label check htmlFor={`automation-update-to-application-note`} className="mb-1 mr-3">
                  Application
                </Label>
              </>
            }
          </FormGroup>
        </>}

      <DeliveryAccordionHeader
        type="button"
        id="scheduling-delivery-toggle"
        aria-expanded={isSchedulingDeliveryOpen}
        aria-controls="scheduling-delivery-section"
        onClick={() => setIsSchedulingDeliveryOpen(!isSchedulingDeliveryOpen)}
      >
        <span>
          Scheduling &amp; Delivery <DeliveryAccordionOptional>(Optional)</DeliveryAccordionOptional>
        </span>
        <DeliveryAccordionChevron className={`fa ${isSchedulingDeliveryOpen ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true" />
      </DeliveryAccordionHeader>

      <Collapse isOpen={isSchedulingDeliveryOpen} id="scheduling-delivery-section">
        <FormGroup className="step-body-text" style={{ marginBottom: '20px' }}>
          <DeliveryOption htmlFor="email-schedule-option" disabled={!isDraft}>
            <DeliveryOptionText>
              <DeliveryOptionTitle>Email schedule</DeliveryOptionTitle>
              <DeliveryOptionDescription>Choose when this email step should send.</DeliveryOptionDescription>
            </DeliveryOptionText>
            <DeliveryOptionCheckbox
              type="checkbox"
              id="email-schedule-option"
              className="automation-delivery-option-checkbox"
              checked={!!formEmail.isSpecificTime}
              disabled={!isDraft}
              onChange={handleScheduleOptionChange}
            />
          </DeliveryOption>
          {formEmail.isSpecificTime && (
            <DeliveryOptionSettings>
              <ScheduleDayButtonRow role="group" aria-label="Days to send">
                {renderDaysComponent(Enum.ListDaysShort)}
              </ScheduleDayButtonRow>
              <div className="w-100 d-flex mt-2">
                <AutomationLabelSchedule className="text-white mt-2 mr-2">
                  at
                </AutomationLabelSchedule>
                <DatePicker
                  id="email-form-input-start-hours"
                  autoComplete="off"
                  disabled={!isDraft}
                  selected={new Date(moment().set(automationStore.convertStringTimeToObject(formEmail.convertedStartHour)))}
                  onChange={date => {
                    let data = { ...formEmail };
                    data.startHour = date;
                    data.convertedStartHour = automationStore.convertStepTimeToString(date)
                    automationStore.setFormEmail(data);
                  }}
                  className="w-100 font-12"
                  showTimeSelect
                  showTimeSelectOnly
                  timeFormat="hh:mm aa"
                  timeIntervals={60}
                  dateFormat="h:mm aa"
                  customInput={<TimeInput isSideBarForm={true} icon={true} />}
                />
              </div>
              <p className="small mt-2 mb-0 font-italic text-white"> Your app's timezone is {generalSettingStore.timeZoneName}.</p>
            </DeliveryOptionSettings>
          )}

          {isEmailBatchingEnabled && (
            <>
              <DeliveryOptionDivider aria-hidden="true">OR</DeliveryOptionDivider>
              <DeliveryOption htmlFor="email-batch-option" disabled={!isDraft}>
                <DeliveryOptionText>
                  <DeliveryOptionTitle>Send email in batches</DeliveryOptionTitle>
                  <DeliveryOptionDescription>Send this email step in batches.</DeliveryOptionDescription>
                </DeliveryOptionText>
                <DeliveryOptionCheckbox
                  type="checkbox"
                  id="email-batch-option"
                  className="automation-delivery-option-checkbox"
                  checked={!!formEmail.isBatchingSend}
                  disabled={!isDraft}
                  onChange={handleBatchingOptionChange}
                />
              </DeliveryOption>
              {formEmail.isBatchingSend && (
                <DeliveryOptionSettings>
                  <Label className="text-white pl-3">What is the maximum number of emails you wish to send in a single batch?</Label>
                  <Select
                    isDisabled={!formEmail.isBatchingSend || !isDraft}
                    id="automation-email-batching-send"
                    className={`automation-email-batching-send pl-3 mb-2 ${!formEmail.isBatchingSend && `automation-disable-dropdown`}`}
                    classNamePrefix="automation-dropdown"
                    placeholder="Select Batch Size"
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldBlockScroll={true}
                    onChange={(e) => {
                      automationStore.setFormEmail({ ...formEmail, batchSize: e.value });
                    }}
                    options={batchSizeOptions}
                    getOptionLabel={(option) => option?.label}
                    getOptionValue={(option) => option?.value}
                    value={formEmail.batchSize ? batchSizeOptions.find(x => x.value === formEmail.batchSize) : batchSizeDefaultValue}
                  />
                  <Label className="text-white pl-3">What duration of delay would you like between each batch?</Label>
                  <Row>
                    <Col md={6}>
                      <Select
                        isDisabled={!formEmail.isBatchingSend || !isDraft}
                        id="automation-email-batching-delay"
                        className={`automation-email-batching-delay pl-3 mb-2 ${!formEmail.isBatchingSend && `automation-disable-dropdown`}`}
                        classNamePrefix="automation-dropdown"
                        placeholder="Select Duration"
                        menuPosition="fixed"
                        menuPlacement="auto"
                        menuShouldBlockScroll={true}
                        onChange={(e) => {
                          const isCustomHours = e.value === 0;
                          automationStore.setFormEmail({
                            ...formEmail,
                            selectedDelayInDays: e.value,
                            selectedDelayInHours: isCustomHours ? selectHoursDefaultValue.value : null,
                            delayInHours: isCustomHours ? selectHoursDefaultValue.value : e.value,
                          });
                        }}
                        options={delayInHoursOptions}
                        getOptionLabel={(option) => option?.label}
                        getOptionValue={(option) => option?.value}
                        value={delayInHoursOptions.find(x => x.value === formEmail.selectedDelayInDays) || delayInHoursDefaultValue}
                      />
                    </Col>
                    <Col md={6}>
                      <Select
                        isDisabled={
                          !formEmail.isBatchingSend ||
                          !isDraft ||
                          formEmail.selectedDelayInDays !== 0
                        }
                        id="automation-email-batching-delay-hours"
                        className={`automation-email-batching-delay-hours pr-3 mb-2`}
                        classNamePrefix="automation-dropdown"
                        placeholder="Select hours"
                        menuPosition="fixed"
                        menuPlacement="auto"
                        menuShouldBlockScroll={true}
                        onChange={(e) => {
                          automationStore.setFormEmail({
                            ...formEmail,
                            selectedDelayInHours: e.value,
                            selectedDelayInDays: 0, // keep delayInHours as 0 to indicate custom hour
                            delayInHours: e.value
                          });
                        }}
                        options={selectHoursOptions}
                        getOptionLabel={(option) => option?.label}
                        getOptionValue={(option) => option?.value}
                        value={selectHoursOptions.find(x => x.value === formEmail.selectedDelayInHours) || selectHoursDefaultValue}
                      />
                    </Col>
                  </Row>
                </DeliveryOptionSettings>
              )}
            </>
          )}
        </FormGroup>
      </Collapse>

      {isDraft && (
        <>
          <SubmitButton
          type={RappidNodeType.Email}
          text={renderEmailNodeLine1(formEmail)}
          text2={renderEmailNodeLine2(formEmail)}
          text3={renderEmailNodeLine3(formEmail)}
          text4={renderEmailNodeLine4(formEmail)}
          roiData={formEmail}
          validateConditions={() => validateNodeAndResetBorder()}
          isDisabled={disableButton} />
        </>
      )}
    </>
  );
}));

export const renderEmailNodeLine1 = (formEmail) => {
  // A chosen template names the card.
  if (formEmail.emailCampaignName) return "Email: " + formEmail.emailCampaignName;

  // No template chosen. This is a real state, not a fault: a recruiter building by hand lays the
  // flow out before filling the emails in, and the assistant leaves the step empty on purpose when
  // the only matching templates are the shared starter emails the company never opened.
  //
  // It used to render as "Email: " with nothing after it, so the card came out blank and read as
  // broken. When the assistant drafted a subject, showing that says what the step is for and what
  // still needs doing.
  if (formEmail.subject) return "Email to write: " + formEmail.subject;

  return "Email: none chosen yet";
};

export const renderEmailNodeLine2 = (formEmail) => {
  if ((formEmail.isFromAtsUser && formEmail.atsUserId) || (formEmail.isFromUser && formEmail.userId)) {
    // The user lists are loaded by the email form, so a canvas opened cold falls back to the mode name.
    const options = buildSpecificUserOptions(automationStore.automationEmailAtsUsers, automationStore.automationEmailUserIds, formEmail.isFromUser ? formEmail.userId : null);
    const user = findSelectedSpecificUser(formEmail, options);
    return "From: " + (user?.fullName || "Specific user");
  }

  if (formEmail.isFromOwner) {
    return "From: " + getOwnerSenderLabel(formEmail.ownerType, automationStore.automationType, automationStore.triggerType);
  }
  return "From: " + formEmail.senderName;
}

export const renderEmailNodeLine3 = (formEmail) => {
  if (formEmail.isSpecificTime) {
    return getScheduleText(formEmail.specificDays, formEmail.convertedStartHour ?? formEmail.startHour);
  } else if (formEmail.isBatchingSend) {
    return getBatchEmailText(formEmail.batchSize, formEmail.delayInHours);
  } else {
    if (formEmail.isUpdateRecord)
      return getRecordUpdate(formEmail.updateRecordType, automationStore.automationType);
  }
}

export const renderEmailNodeLine4 = (formEmail) => {
  return formEmail.isSpecificTime && formEmail.isUpdateRecord
    ? getRecordUpdate(formEmail.updateRecordType, automationStore.automationType)
    : "";
}


export default EmailForm;
